import { validateMappingForm, validateOrganisationForm } from '../../../../components/Management/validations.ts';

const phoneRegex = /^([1-9]{1}[0-9]{1,14})$/;

function validateWizardUserRow(row, index) {
  const errors = {};
  const prefix = `user_${index}`;
  if (!row.first_name?.trim()) errors[`${prefix}_first_name`] = 'First name is required';
  else if (!/^[a-zA-Z\s]+$/.test(row.first_name.trim())) {
    errors[`${prefix}_first_name`] = 'First name must contain only letters';
  }
  if (!row.last_name?.trim()) errors[`${prefix}_last_name`] = 'Last name is required';
  else if (!/^[a-zA-Z\s]+$/.test(row.last_name.trim())) {
    errors[`${prefix}_last_name`] = 'Last name must contain only letters';
  }
  if (!row.username?.trim()) errors[`${prefix}_username`] = 'Username is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email || '')) {
    errors[`${prefix}_email`] = 'Please enter a valid email address';
  }
  if (!phoneRegex.test(row.mobile || '')) {
    errors[`${prefix}_mobile`] = 'Mobile number must be a valid 10-digit number';
  }
  return errors;
}

/** Clone source, name, and permission tables — before profile is created in DB. */
export function validateProfileSetup(draft) {
  const errors = {};
  const pu = draft.profile_users;
  if (!pu?.clone_from_profile_id) {
    errors.clone_from_profile_id = 'Select a profile to clone from';
  }
  if (!pu?.profile_name?.trim()) {
    errors.profile_name = 'New profile name is required';
  }
  if (pu?.clone_from_profile_id && draft.objectPermissions.length === 0) {
    errors.objectPermissions = 'Wait for profile permissions to load, or re-select the profile';
  }
  if (pu?.clone_from_profile_id && draft.generalPermissions.length === 0) {
    errors.generalPermissions = 'Wait for profile permissions to load, or re-select the profile';
  }
  return errors;
}

export function validateStep(stepId, draft, options = {}) {
  const errors = {};
  switch (stepId) {
    case 1: {
      const o = draft.organisation;
      const mgmtErrors = validateOrganisationForm({
        name: o.name,
        phone: o.mobile,
        email: o.email,
        category: o.category,
        business_type: o.business_type,
        business_subtype: o.business_subtype,
      });

      if (mgmtErrors.name) errors.name = mgmtErrors.name;
      if (mgmtErrors.phone) errors.mobile = mgmtErrors.phone;
      if (mgmtErrors.email) errors.email = mgmtErrors.email;
      if (mgmtErrors.category) errors.category = mgmtErrors.category;
      if (mgmtErrors.business_type) errors.business_type = mgmtErrors.business_type;
      if (mgmtErrors.business_subtype) errors.business_subtype = mgmtErrors.business_subtype;
      if (!o.license_id?.trim()) errors.license_id = 'Select a license plan';
      break;
    }
    case 2: {
      const a = draft.account;

      if (a.name && a.name.length > 50) {
        errors.name = 'Account name must be 50 characters or less.';
      }
      if (a.phone && !/^\d{10,12}$/.test(a.phone.replace(/\s+/g, ''))) {
        errors.phone = 'Phone number must be 10-12 digits.';
      }
      if (a.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) {
        errors.email = 'Please enter a valid email address.';
      }
      if (a.address && a.address.length > 100) {
        errors.address = 'Address must be 100 characters or less.';
      }
      if (a.reference_name && a.reference_name.length > 50) {
        errors.reference_name = 'Reference name must be 50 characters or less.';
      }
      if (a.reference_phone && !/^\d{10,12}$/.test(a.reference_phone.replace(/\s+/g, ''))) {
        errors.reference_phone = 'Reference phone must be 10-12 digits.';
      }
      if (a.reference_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.reference_email)) {
        errors.reference_email = 'Please enter a valid reference email address.';
      }
      if (a.comments && a.comments.length > 500) {
        errors.comments = 'Comments must be 500 characters or less.';
      }

      if (a.contacts && Array.isArray(a.contacts)) {
        a.contacts.forEach((contact, idx) => {
          if (contact.name && contact.name.length > 50) {
            errors[`contact_${idx}_name`] = `Contact ${idx + 1} name must be 50 characters or less.`;
          }
          if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
            errors[`contact_${idx}_email`] = `Contact ${idx + 1} email is invalid.`;
          }
          if (contact.phone && !/^\d{10,12}$/.test(contact.phone.replace(/\s+/g, ''))) {
            errors[`contact_${idx}_phone`] = `Contact ${idx + 1} phone must be 10-12 digits.`;
          }
          if (contact.designation && contact.designation.length > 100) {
            errors[`contact_${idx}_designation`] = `Contact ${idx + 1} designation must be 100 characters or less.`;
          }
          if (contact.department && contact.department.length > 100) {
            errors[`contact_${idx}_department`] = `Contact ${idx + 1} department must be 100 characters or less.`;
          }
        });
      }
      break;
    }
    case 3: {
      const m = draft.organisation_mapping;
      const mappingErrors = validateMappingForm({
        firstOrganisation: m.firstOrganisation,
        secondOrganisation: m.secondOrganisation,
      });
      Object.assign(errors, mappingErrors);
      break;
    }
    case 4: {
      Object.assign(errors, validateProfileSetup(draft));
      if (!options.profileId) {
        errors.profile = 'Create the profile before continuing (use Create Profile button)';
      }
      if (draft.users.length === 0) {
        errors.users = 'Add at least one user after the profile is created';
      } else {
        draft.users.forEach((row, idx) => {
          Object.assign(errors, validateWizardUserRow(row, idx));
        });
        if (Object.keys(errors).some((k) => k.startsWith('user_'))) {
          errors.users = errors.users || 'Fix user details below';
        }
      }
      break;
    }
    case 5: {
      const wa = draft.whatsapp;
      if (!wa.app_id.trim()) errors.app_id = 'App ID is required';
      if (!wa.app_name.trim()) errors.app_name = 'App name is required';
      if (!wa.app_type.trim()) errors.app_type = 'App type is required';
      if (!wa.wa_phone_number.trim()) errors.wa_phone_number = 'WhatsApp number is required';
      if (!wa.wa_phone_number_id.trim()) {
        errors.wa_phone_number_id = 'Phone Number ID is required';
      }
      if (!wa.waba_id.trim()) errors.waba_id = 'WABA ID is required';
      if (!wa.user_access_token.trim()) {
        errors.user_access_token = 'User access token is required';
      }
      break;
    }
    case 6: {
      const campaigns = draft.campaign_setup.campaigns;
      if (campaigns.length === 0) errors.goals = 'Select at least one campaign goal';
      campaigns.forEach((c) => {
        if (!c.name.trim()) errors[`name_${c.goal}`] = 'Campaign name is required';
      });
      break;
    }
    case 7: {
      const campaigns = draft.campaign_setup.campaigns;
      if (campaigns.length === 0) {
        errors.formFields = 'Complete campaign goals first';
        break;
      }
      campaigns.forEach((c) => {
        if (c.form_fields.length === 0) {
          errors[`form_${c.goal}`] = 'Add at least one form field';
        }
      });
      break;
    }
    default:
      break;
  }
  return errors;
}
